import { useQuery, queryOptions } from '@tanstack/react-query'

import { Query } from '../services/Query'
import { fetchData, fetchDataId, saveData, deleteDataId, emptyQuery } from '../services/MiddleTierData'
import { Notify } from '../services/Notify'
import { CurrentUser } from '../services/SessionService'
import { TSClient } from '../services/TanStackQuery'
import { update } from 'lodash'
import { useEffect, useState } from 'react'
import { CaseRecord } from '../models/Case'
import { ActivityRecord } from '../models/Activity'
import { ConvertDateForDBOn, NowDB } from '../utils'
import { updateCaseSummary, updateCaseSummaryById } from '../services/CaseSummary'

export function InvalidateQueries(dataType: string) {
  TSClient.invalidateQueries({ queryKey: [dataType] })
}

export function useQueryData(dataType: string, query: Query = {}, enabled = true, stale = 5 * 60 * 1000) {
  return useQuery({ queryKey: [dataType, query], queryFn: async () => await fetchData(dataType, query), enabled: enabled, staleTime: stale })
}

export function useQueryDataId(dataType: string, id: string, query: Query = {}, stale = 5 * 60 * 1000) {
  return useQuery({ queryKey: [dataType, id], queryFn: async () => await fetchDataId(dataType, id, query), enabled: !!id, staleTime: stale })
}

export async function recordActivity(type: string, desc: string, caseId?: string, dataType?: string, id?: string, user?: string) {
  const act = { 
    On: NowDB(), 
    Type: type, 
    Description: desc, 
    ForId: dataType + ':' + id,
    CaseId: caseId || '', 
    createdBy: user || CurrentUser().fullName, 
    updatedBy: user || CurrentUser().fullName,
  }

  saveData('Activity', act)
}

const skip = "Activity;Communication;Document;Bug;CaseSummary;DataHistory;Search;Feature;Folder;ImpactType;PathDoc;Summary;Template"
async function recordDataActivity(action: string, dataType: string, id: string, caseId: string) {
  if (skip.includes(dataType)) return
  recordActivity(action, dataType + ' record ' + action, caseId, dataType, id)
}

export function upsertBlocking(dataType: string, ob: any, update: boolean = true) {
  (async () => {
    await upsert(dataType, ob, update)
  })()
}

export function upsertPromise(dataType: string, ob: any, update: boolean = true) {
    return upsert(dataType, ob, update)
}

export async function upsert(dataType: string, ob: any, update: boolean = true, user: string = "") {
  user = user || CurrentUser().fullName
  const trimmed = trimObjectStrings(ob)
  if (!trimmed.createdBy) trimmed.createdBy = user
  trimmed.updatedBy = user
  console.log('trimmed', ob, trimmed)
  
  let result: any = null
  try {
    result = await saveData(dataType, trimmed)
  }
  catch (e: any) {
    console.error("Error in upsert for " + dataType, e)
    Notify('error', dataType + ' failed to save: ' + e?.message)
    return null
  }

  if (result && dataType !== 'Activity') {
    let caseId = dataType === "Case" ? result : (ob?.CaseId ? ob?.CaseId : ob?.ForId?.startsWith('Case:') ? ob?.ForId?.split(':')[1] : "")
    recordDataActivity(ob._id ? 'Update' : 'Create', dataType, result, caseId)
  }

  if (result) {
    Notify('success', dataType + ' ' + (ob._id ? 'updated' : 'created'))
  } else {
    Notify('error', dataType + ' failed to save')
  }

  if (!update) return result
  
  // ****** If dataType === Case or Contact, then must also update Search
  updateSearch(dataType, ob, result)

  // ****** If dataType === Case, then must also update CaseSummary
  if (dataType === 'Case') {
    await updateCaseSummary(ob as CaseRecord, null, null)
    TSClient.invalidateQueries({ queryKey: ['CaseSummary'] })
  }
  // ****** If dataType === Activity, update CaseSummary to update alerts
  if (dataType === 'Activity' || dataType === 'Document' || dataType === 'Offer') {
    await updateCaseSummaryById(ob?.CaseId, null, ob as ActivityRecord)
    TSClient.invalidateQueries({ queryKey: ['CaseSummary'] })
  }

  TSClient.invalidateQueries({ queryKey: [dataType] })
  if (dataType === 'Activity') TSClient.invalidateQueries({ queryKey: ['ActivitySortedCaseName'] })
    
  return result
}

export async function deleteId(dataType: string, id: string) {
  try {
    const result = await deleteDataId(dataType, id)
    // ****** If dataType === Case, Contact or Intake, then must also update Search
    updateSearch(dataType, null, id)
    if (dataType ==='Case') await updateCaseSummary(id, null, null)
  
      // if (dataType !== 'Activity') recordDataActivity('Delete', dataType, id, "")
      Notify('success', dataType + ' deleted')
  
      TSClient.invalidateQueries({ queryKey: [dataType] })
      if (dataType === 'Activity') TSClient.invalidateQueries({ queryKey: ['ActivitySortedCaseName'] })
      return result  
  } catch (e: any) {
    Notify('error', dataType + ' failed deletion (' + id + '): ' + e?.message)
  }
}

// https://tanstack.com/query/latest/docs/framework/react/reference/useQuery
const defaults = queryOptions({
  queryKey: ['groups'],
  queryFn: () => {},
  staleTime: 5 * 60 * 1000,
  enabled: true,
  retry: 1,
  // select: (data) => data.map((group) => group.name),
  // initialData: [],
  // placeholderData: [],
  // throwOnError: (error, query) => new Error('An error occurred'),
})

export async function rebuildSearch() {
  // Clear the Search collection
  // Not sure yet the best way to do this, so for now, just delete all Search records
  const searchRecords = await fetchData('Search')
  for (const record of searchRecords.data) {
    await deleteDataId('Search', record._id)
  }
  Notify('success', 'Search records cleared')

  const ps = await fetchData('Contact')
  for await (const o of ps.data) {
    await updateSearch('Contact', o, o._id)
  }

  // const is = await fetchData('Intake')
  // for await (const o of is.data) {
  //   await updateSearch('Intake', o, o._id)
  // }

  const cs = await fetchData('Case')
  for await (const o of cs.data) {
    await updateSearch('Case', o, o._id)
  }
  
  TSClient.invalidateQueries({ queryKey: ['Search'] })
  Notify('success', 'Search records rebuilt')
}

async function updateSearch(dataType: string, ob: any, id: string) {
  if (dataType === 'Case' || dataType === 'Contact') { //} || dataType === 'Intake') {
    return fetchData('Search', { Paging: {Page: 1, PageSize: 1}, 
      Where: [{kind: 'equals', field: 'Id', value: id}, {kind: 'equals', field: 'Class', value: dataType}]}).then(async (r: any) => { 
        var search = r.data[0]
        if (!ob && search) {
          // If ob is null, then we are deleting the search record 
            await deleteDataId('Search', search._id)
            Notify('success', dataType + ' search record deleted')
            return
        }
        if (!search) search = { Class: dataType, Id: id }
        switch (dataType) {
          case 'Case':  
            search.Status = ob?.Status
            search.Name = ob?.Name
            search.Client = ob?.Client
            search.Terms = [ob?.Name?.toLowerCase(), ob.Details?.FirstPartyInsurance?.ClaimNumber?.toLowerCase(), ob.Details?.ThirdPartyInsurance?.ClaimNumber?.toLowerCase()].join(' ')
    // return o.get('Name') + ((client) ? appendValues(" ", client.FirstName, client.LastName, client.Phone, client.Email) : "") + 
    // appendValues(" ", details && details.FirstPartyInsurance && details.FirstPartyInsurance.ClaimNumber, details && details.ThirdPartyInsurance && details.ThirdPartyInsurance.ClaimNumber)        
            break
          case 'Contact': 
            search.Name = ob?.FirstName + ' ' + ob?.LastName
            search.Status = "Created"
            search.Client = search.Name
            search.Terms = [ob?.FirstName?.toLowerCase(), ob?.LastName?.toLowerCase(), ob?.Phone?.toLowerCase(), ob?.Email?.toLowerCase()].join(' ')
            break
          // case 'Intake':
          //   search.Name = ob?.Name
          //   search.Status = ob?.Status
          //   search.Client = ob?.Details?.Contact?.FirstName + ' ' + ob?.Details?.Contact?.LastName
          //   search.Terms = [ob?.Details?.Contact?.FirstName?.toLowerCase(), ob?.Details?.Contact?.LastName?.toLowerCase(), ob?.Details?.Contact?.Phone?.toLowerCase(), ob?.Details?.Contact?.Email?.toLowerCase()].join(' ')
          //   break
        }

        await saveData('Search', search)
        TSClient.invalidateQueries({ queryKey: ['Search'] })
      }
    )
  }          
}

export function useTextSearch(text: string, type: string, currentPage: number, pageSize: number) {
  const [data, setData] = useState<any[]>()
  const [count, setCount] = useState(0)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  useEffect(() => {
    async function search() {
      // const query: Query = { Paging: {Page: currentPage, PageSize: pageSize}, Where: [{kind: 'contains', field: 'Terms', value: text.toLowerCase()}] }
      const query: Query = { Paging: {Page: currentPage, PageSize: pageSize}, Where: [{kind: 'search', field: 'default', value: text.toLowerCase()}] }
      if (type !== 'All') query?.Where?.push({kind: 'equals', field: 'Class', value: type})
      const results = await fetchData('Search', query)
      setData(results.data)
      setCount(results.count)
      setLoading(false)
    }

    if (text && text.length > 1 && !isLoading) { 
      setLoading(true)
      search().catch((error) => { setError(error) }) }
  }, [text, type, currentPage, pageSize])

  return {data, count, isLoading, error}
}

/**
 * Recursively trims all string values within an object or array.
 * Returns a new structural copy without mutating the input data.
 */
function trimObjectStrings(input: any): any {
  // Handle null or undefined immediately
  if (input === null || input === undefined) {
    return input
  }

  // Handle strings
  if (typeof input === 'string') {
    return input.trim()
  }

  // Handle arrays
  if (Array.isArray(input)) {
    return input.map((item) => trimObjectStrings(item))
  }

  // Handle nested objects (excluding special objects like Dates or Regex)
  if (typeof input === 'object') {
    const trimmedObj: Record<string, any> = {}
    for (const key of Object.keys(input)) {
      trimmedObj[key] = trimObjectStrings(input[key])
    }

    return trimmedObj
  }

  // Return primitive values (numbers, booleans, etc.) as-is
  return input as any
}

// [
//   {
//     $search: {
//       index: "default",
//       text: {
//         query: "one bedroom rental",
//         path: {
//           wildcard: "*"
//         }
//       }
//     }
//   }
// ]

// const query = [
//   {
//     $search: 
//     {
//       text: {
//         query: "baseball",
//         path: "plot",
//       },
//     }
//   },
//   {
//     $limit: 2,
//   },
//   {
//     $project: {
//       _id: 0,
//       title: 1,
//       plot: 1,
//     },
//   },
// ]
